import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  FileUpload,
  Form,
  FormItem,
  SubmitButton,
} from '../../../../components';
import { PRESENTATION_LABELS } from '../constants';
import {
  validatePresentationTitle,
  validatePresentationDescription,
  validatePresentationFile,
} from '../utils/validator';
import type { PresentationFormData } from '../types';

interface PresentationFormProps {
  presentation?: {
    id?: string;
    title?: string;
    description?: string;
  } | null;
  onSave: (data: PresentationFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

type FormProps = {
  title: string;
  description: string;
  file?: File;
};

const defaultFormValues: FormProps = {
  title: '',
  description: '',
  file: undefined,
};

export const PresentationForm: React.FC<PresentationFormProps> = ({
  presentation,
  onSave,
  onCancel,
  loading = false,
}) => {
  const [formValues, setFormValues] = useState<FormProps>(defaultFormValues);

  useEffect(() => {
    if (presentation) {
      setFormValues({
        title: presentation.title || '',
        description: presentation.description || '',
        file: undefined,
      });
    } else {
      setFormValues(defaultFormValues);
    }
  }, [presentation]);

  const handleSaveData = () => {
    const formData: PresentationFormData = {
      title: formValues.title,
      description: formValues.description,
      file: formValues.file,
    };
    onSave(formData);
  };

  return (
    <div className="relative bg-white w-full rounded-xl p-6 flex min-h-[300px] overflow-hidden">
      <Form initialValue={formValues} onChange={setFormValues}>
        <div className="w-full flex flex-col gap-4">
          <FormItem
            name="title"
            label={PRESENTATION_LABELS.PRESENTATION_TITLE}
            required
            validate={validatePresentationTitle}
            inline={false}
          >
            <Input placeholder="Enter presentation title" disabled={loading} />
          </FormItem>
          {!presentation && (
            <div className="flex-1 min-h-[200px] border border-dashed border-border rounded-md flex flex-col justify-center items-center text-medium mb-4">
              <span className="text-primary">
                <FormItem
                  name="file"
                  label=""
                  required
                  validate={validatePresentationFile}
                  inline={false}
                  renderError={() => null}
                >
                  <FileUpload loading={loading} />
                </FormItem>
              </span>
            </div>
          )}

          <FormItem
            name="description"
            label={PRESENTATION_LABELS.DESCRIPTION}
            validate={validatePresentationDescription}
            inline={false}
          >
            <Input
              multiline
              placeholder="Enter presentation description"
              disabled={loading}
            />
          </FormItem>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              label="Cancel"
              onClick={onCancel}
              disabled={loading}
              className="bg-gray-500 hover:bg-gray-600 text-white"
            />
            <SubmitButton
              label={presentation ? 'Update' : 'Create'}
              disabled={loading}
              loading={loading}
              onClick={handleSaveData}
            />
          </div>
        </div>
      </Form>
    </div>
  );
};
